import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { trpc } from '../../lib/trpc';

/**
 * Widget de suporte com IA (Fase 6) - SÓ no ambiente do cliente (nunca no
 * console admin; o gate fica no Shell). Botão flutuante + painel de chat com
 * histórico persistido, indicador "digitando", estados de carregando/erro, e
 * "Falar com um humano" sempre visível.
 */
type Autor = 'usuario' | 'agente' | 'sistema';
interface Msg {
  id: string;
  autor: Autor;
  corpo: string;
}

function Bolha({ autor, corpo }: { autor: Autor; corpo: string }) {
  if (autor === 'sistema') {
    return <div className="my-1 text-center text-xs text-ink-4">{corpo}</div>;
  }
  const usuario = autor === 'usuario';
  return (
    <div className={`flex ${usuario ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] whitespace-pre-line rounded-[14px] px-3 py-2 text-sm"
        style={
          usuario
            ? { background: 'var(--accent)', color: '#fff' }
            : { background: 'var(--surface-2)', color: 'var(--ink)' }
        }
      >
        {corpo}
      </div>
    </div>
  );
}

export function SupportWidget() {
  const [aberto, setAberto] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [status, setStatus] = useState<string>('ativa');
  const [input, setInput] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [digitando, setDigitando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [carregado, setCarregado] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const r = await trpc.suporte.conversa.query();
      setMsgs(r.mensagens as Msg[]);
      setStatus(r.status);
      setCarregado(true);
    } catch {
      setErro('Não foi possível carregar o suporte.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (aberto && !carregado && !carregando) void carregar();
  }, [aberto, carregado, carregando]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, digitando]);

  async function enviar() {
    const corpo = input.trim();
    if (!corpo || digitando) return;
    setInput('');
    setErro(null);
    setDigitando(true);
    setMsgs((m) => [...m, { id: `tmp-${m.length}`, autor: 'usuario', corpo }]);
    try {
      const r = await trpc.suporte.enviar.mutate({ corpo });
      setMsgs(r.mensagens as Msg[]);
      setStatus(r.status);
    } catch (e) {
      setErro((e as Error).message ?? 'Erro ao enviar. Tente novamente.');
    } finally {
      setDigitando(false);
    }
  }

  async function escalar() {
    if (digitando) return;
    setErro(null);
    setDigitando(true);
    try {
      const r = await trpc.suporte.escalar.mutate();
      setMsgs(r.mensagens as Msg[]);
      setStatus(r.status);
    } catch {
      setErro('Não foi possível acionar o atendente.');
    } finally {
      setDigitando(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void enviar();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-label="Suporte"
        data-testid="suporte-fab"
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105"
        style={{ background: 'var(--accent)' }}
      >
        {aberto ? (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
            <path d="M4 5h16v11H8l-4 4V5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {aberto && (
        <div
          data-testid="suporte-panel"
          className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-surface sm:inset-auto sm:bottom-24 sm:right-5 sm:z-40 sm:h-[520px] sm:w-[360px] sm:max-w-[calc(100vw-2.5rem)] sm:rounded-[16px] sm:border sm:border-line sm:shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
            <span className="text-sm font-semibold text-ink">Assistente AMA Capital</span>
            <div className="ml-auto flex items-center gap-2">
              {status === 'escalada' && <span className="text-[11px] text-ink-4">escalado</span>}
              <button
                type="button"
                onClick={() => setAberto(false)}
                aria-label="Fechar suporte"
                data-testid="suporte-fechar"
                className="flex h-9 w-9 items-center justify-center rounded-[10px] text-ink-3 hover:bg-bg-warm hover:text-ink sm:hidden"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                  <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-2 overflow-auto px-3 py-3">
            {carregando && <div className="text-center text-sm text-ink-4">Carregando…</div>}
            {erro && (
              <div className="rounded-[10px] px-3 py-2 text-sm" style={{ background: 'var(--risk-soft)', color: 'var(--risk)' }}>
                {erro}{' '}
                <button type="button" onClick={carregar} className="font-medium underline">
                  Tentar de novo
                </button>
              </div>
            )}
            {msgs.map((m) => (
              <Bolha key={m.id} autor={m.autor} corpo={m.corpo} />
            ))}
            {digitando && <div className="text-left text-xs text-ink-4">digitando…</div>}
            <div ref={fimRef} />
          </div>

          <div className="border-t border-line px-3 py-2.5">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                maxLength={2000}
                placeholder="Escreva sua dúvida…"
                data-testid="suporte-input"
                className="h-10 flex-1 rounded-[10px] border border-line bg-surface px-3 text-sm text-ink"
              />
              <button
                type="button"
                onClick={enviar}
                disabled={digitando || !input.trim()}
                data-testid="suporte-enviar"
                className="h-10 rounded-[10px] px-3 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--accent)' }}
              >
                Enviar
              </button>
            </div>
            <button
              type="button"
              onClick={escalar}
              disabled={digitando}
              data-testid="suporte-escalar"
              className="mt-2 w-full text-center text-xs font-medium text-ink-3 hover:text-ink"
            >
              Falar com um humano
            </button>
          </div>
        </div>
      )}
    </>
  );
}
